import React, { useState, useEffect } from 'react';
import { Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isFavorite, toggleFavorite } from '@/lib/favorites';
import { fireEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

interface FavButtonProps {
  domain: string;
  size?: 'sm' | 'md';
  variant?: 'solid' | 'ghost';
  className?: string;
}

export default function FavButton({
  domain,
  size = 'md',
  variant = 'ghost',
  className,
}: FavButtonProps) {
  const [isFav, setIsFav] = useState(false);

  // Load initial favorite state
  useEffect(() => {
    setIsFav(isFavorite(domain));
  }, [domain]);

  // Listen for favorites updates from other components
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      setIsFav(isFavorite(domain));
    };

    window.addEventListener('favorites-updated', handleFavoritesUpdate);
    return () => {
      window.removeEventListener('favorites-updated', handleFavoritesUpdate);
    };
  }, [domain]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const isNowFavorite = toggleFavorite(domain);
    setIsFav(isNowFavorite);

    // Track analytics
    fireEvent(isNowFavorite ? 'fav_add' : 'fav_remove', { domain });
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
  };

  return (
    <Button
      variant={variant === 'solid' ? 'outline' : 'ghost'}
      size="icon"
      onClick={handleToggle}
      className={cn(
        sizeClasses[size],
        'transition-all duration-200 hover:scale-105',
        isFav && variant === 'solid' && 'bg-primary/10 border-primary/20 hover:bg-primary/15',
        isFav && variant === 'ghost' && 'text-primary hover:text-primary/80 hover:bg-primary/5',
        className,
      )}
      aria-pressed={isFav}
      aria-label={isFav ? `Remove ${domain} from favorites` : `Add ${domain} to favorites`}
      title={isFav ? 'Saved' : 'Save'}
    >
      {isFav ? (
        <Check
          size={iconSizes[size]}
          className={cn(
            'transition-all duration-200',
            'text-primary',
          )}
        />
      ) : (
        <Plus
          size={iconSizes[size]}
          className={cn(
            'transition-all duration-200',
            'text-muted-foreground',
          )}
        />
      )}
    </Button>
  );
}
